import { PRODUCT_SALES_GROUP_MAP } from "../../utils/constantFiles/constant";
import {
  findProductGroupDictEntry,
  isTyvasoProductSelection,
  productGroupKeysMatch,
} from "../MyPerformance/myPerformanceHelpers";

const DEVICE_MIX_COLORS = ["#396D87", "#8FB8CC", "#00A63E", "#f1c40f"];
const INDICATION_MIX_COLORS = ["#e67e22", "#f1c40f", "#396D87", "#6B7280"];
const ATTAINMENT_COLORS = ["#396D87", "#E87722", "#00A63E", "#6B7280"];

/** Competitive Dynamics shows at most this many claimants. */
const MAX_COMPETITIVE_CLAIMANTS = 5;

const BREADTH_DEPTH_ORDER = [
  { key: "non_prescribers", fallbackLabel: "Non-Prescribers", color: "#6B7280" },
  { key: "breadth_writers", fallbackLabel: "Breadth Writers", color: "#e67e22" },
  { key: "depth_writers", fallbackLabel: "Depth Writers", color: "#00A63E" },
];

/** Display order for known products, e.g. Remodulin before Orenitram. */
const PRODUCT_DISPLAY_ORDER = [
  ...new Set(
    Object.values(PRODUCT_SALES_GROUP_MAP).map((entry) => entry.product),
  ),
];

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function titleCase(value) {
  const text = (value ?? "").toString().trim();
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

function productOrderIndex(key) {
  const index = PRODUCT_DISPLAY_ORDER.findIndex((product) =>
    productGroupKeysMatch(product, key),
  );
  return index === -1 ? PRODUCT_DISPLAY_ORDER.length : index;
}

/** Sorts payload keys into display order, keeping unknown keys at the end. */
function sortProductKeys(keys) {
  return [...keys].sort((a, b) => {
    const byOrder = productOrderIndex(a) - productOrderIndex(b);
    return byOrder || a.localeCompare(b);
  });
}

/** Products the rep's group sells — fallback when the report has no rows. */
function productKeysForGroup(selectedProduct) {
  const key = (selectedProduct ?? "").toString().trim();
  if (!key) return [];

  const products = [];
  Object.entries(PRODUCT_SALES_GROUP_MAP).forEach(([mapKey, entry]) => {
    const belongsToSelection =
      productGroupKeysMatch(key, mapKey) ||
      productGroupKeysMatch(key, entry.salesGroup);
    if (belongsToSelection && !products.includes(entry.product)) {
      products.push(entry.product);
    }
  });

  return products.length ? products : [key];
}

function mapSplit(label, segments, palette) {
  const rows = Array.isArray(segments) ? segments : [];
  if (!rows.length) return null;

  return {
    label,
    segments: rows.map((segment, index) => ({
      name: segment?.label ?? "",
      value: toNumber(segment?.percentage),
      color: palette[index % palette.length],
    })),
  };
}

function hasSplitData(entry) {
  return Boolean(entry?.device_mix || entry?.indication_mix);
}

/**
 * key_metrics is keyed by ProductGroup (TYVASO / REMODULIN / ORENITRAM) while
 * selectedProduct can be a sales group (CPS / ALD / ROCS), so the report's own
 * product keys are tried before falling back to a flat payload.
 */
function findKeyMetricsBlock(keyMetrics, selectedProduct, productKeys) {
  for (const key of [selectedProduct, ...productKeys]) {
    const entry = findProductGroupDictEntry(keyMetrics, key);
    if (hasSplitData(entry)) return entry;
  }
  return hasSplitData(keyMetrics) ? keyMetrics : null;
}

function mapProductSplits(keyMetrics, selectedProduct, productKeys) {
  if (!keyMetrics || typeof keyMetrics !== "object") return [];

  const block = findKeyMetricsBlock(keyMetrics, selectedProduct, productKeys);
  if (!block) return [];

  // PAH vs PH-ILD only splits Tyvaso; Remodulin and Orenitram are PAH-only.
  const hasIndicationSplit = productKeys.some(isTyvasoProductSelection);

  return [
    mapSplit("Method of Delivery Mix", block?.device_mix, DEVICE_MIX_COLORS),
    hasIndicationSplit
      ? mapSplit("Indication Mix", block?.indication_mix, INDICATION_MIX_COLORS)
      : null,
  ].filter(Boolean);
}

function mapBreadthDepth(lengthBreadth) {
  if (!lengthBreadth) return [];

  return BREADTH_DEPTH_ORDER.filter(({ key }) => lengthBreadth[key] != null).map(
    ({ key, fallbackLabel, color }) => ({
      label: lengthBreadth[key]?.label ?? fallbackLabel,
      count: toNumber(lengthBreadth[key]?.count),
      definition: lengthBreadth[key]?.criteria ?? "",
      color,
    }),
  );
}

/**
 * One Attainment QTD bar per key in the report — two for a ROCS rep
 * (REMODULIN + ORENITRAM), one for ALD / CPS (TYVASO).
 */
function mapAttainmentProducts(attainmentDict) {
  if (!attainmentDict || typeof attainmentDict !== "object") return [];

  return sortProductKeys(Object.keys(attainmentDict)).map((key, index) => ({
    key,
    name: titleCase(key),
    current: toNumber(attainmentDict[key]?.Actuals),
    goal: toNumber(attainmentDict[key]?.Goals),
    color: ATTAINMENT_COLORS[index % ATTAINMENT_COLORS.length],
  }));
}

/**
 * Top writers with one referral column per product in the report. Columns
 * follow the payload's own keys, so they line up with whatever the API
 * returns while this section is still static sample data.
 */
function mapTopWriters(advocatesDict) {
  if (!advocatesDict || typeof advocatesDict !== "object") {
    return { columns: [], rows: [] };
  }

  const columns = sortProductKeys(Object.keys(advocatesDict)).map((key) => ({
    key,
    label: titleCase(key),
  }));

  const rowsByHcp = new Map();
  columns.forEach((column) => {
    const block = advocatesDict[column.key];
    const rows = Array.isArray(block?.Increase) ? block.Increase : [];

    rows.forEach((row) => {
      const name = row?.HCP ?? "";
      if (!name) return;
      const existing = rowsByHcp.get(name) || { name, values: {}, rank: 0 };
      existing.values[column.key] = toNumber(row?.Change_in_Metric);
      existing.rank = existing.rank || toNumber(row?.RANK);
      rowsByHcp.set(name, existing);
    });
  });

  const total = (row) =>
    columns.reduce((sum, column) => sum + toNumber(row.values[column.key]), 0);

  const rows = [...rowsByHcp.values()]
    .sort((a, b) => total(b) - total(a) || a.rank - b.rank)
    .map(({ name, values }) => ({ name, values }));

  return { columns, rows };
}

/**
 * Raw row shape returned by /kpi_metrics/qbr_report.competitive_dynamics.
 * @typedef {object} CompetitiveDynamicsRow
 * @property {string} HCP_Name
 * @property {string} Details
 * @property {string} Date
 * @property {string|number} New_Claims
 */

const EMPTY_COMPETITIVE_DYNAMICS = { subtitle: "", claimants: [] };

function mapCompetitiveDynamics(competitiveData, selectedProduct) {
  // Live flat-list format from /kpi_metrics/qbr_report.competitive_dynamics:
  //   [{ HCP_Name, Details, Date, New_Claims }, ...]
  // The backend query filters to nbaidentifier = 'S10-B_Y' (Yutrepia), so the
  // subtitle is fixed accordingly. Empty / missing results return an empty
  // structure so the TerritoryOverviewCard hides the panel (guarded by
  // claimants.length > 0) instead of showing dummy names.
  if (Array.isArray(competitiveData)) {
    if (!competitiveData.length) return EMPTY_COMPETITIVE_DYNAMICS;
    return {
      subtitle: "Top Yutrepia claimants this quarter",
      claimants: competitiveData
        .slice(0, MAX_COMPETITIVE_CLAIMANTS)
        .map((row) => {
          const rawName = (row?.HCP_Name ?? "").toString().trim();
          return {
            name: rawName ? `Dr. ${rawName}` : "",
            claims: toNumber(row?.New_Claims),
            note: row?.Details ?? "",
          };
        }),
    };
  }

  // Legacy per-product-group dict format — kept for backward compatibility.
  const block = findProductGroupDictEntry(competitiveData, selectedProduct);
  if (!block) return EMPTY_COMPETITIVE_DYNAMICS;

  const claimants = Array.isArray(block.claimants) ? block.claimants : [];
  return {
    subtitle: block.subtitle ?? "",
    claimants: claimants.slice(0, MAX_COMPETITIVE_CLAIMANTS).map((row) => ({
      name: row?.name ?? "",
      claims: toNumber(row?.claims),
      note: row?.note ?? "",
    })),
  };
}

/**
 * Maps the `/kpi_metrics/qbr_report` payload onto the TerritoryOverview shape
 * consumed by TerritoryOverviewCard. attainment_qtd_performance_summary,
 * territory_goal, key_metrics, and competitive_dynamics are live from the API;
 * length_breadth and top_product_advocates_growers are still static sample
 * data from the backend. When competitive_dynamics is empty/missing, the
 * mapper returns an empty structure so the panel hides (no dummy fallback).
 *
 * @param {object} params
 * @param {object|null} params.qbrReportResponse Raw thunk payload.
 * @param {string} params.selectedProduct Product group whose block to read.
 * @param {object} [params.strategyFields] Locally edited, non-API fields.
 * @returns {import("./qbrMockData").TerritoryOverview}
 */
export function mapQbrReportToTerritoryOverview({
  qbrReportResponse,
  selectedProduct,
  strategyFields = {},
}) {
  const report = qbrReportResponse?.qbr_report ?? {};

  const attainmentProducts = mapAttainmentProducts(
    report.attainment_qtd_performance_summary,
  );
  // Attainment is live, so its keys are the source of truth for which
  // products this territory sells; fall back to the rep's assigned group.
  const productKeys = attainmentProducts.length
    ? attainmentProducts.map((product) => product.key)
    : productKeysForGroup(selectedProduct);

  const territoryGoals = report.territory_goal ?? {};
  const quarterlyGoalTotal = productKeys.reduce(
    (sum, key) => sum + toNumber(findProductGroupDictEntry(territoryGoals, key)?.Goals),
    0,
  );

  return {
    quarterlyGoal: `${quarterlyGoalTotal} New Starts`,
    attainmentCurrent: attainmentProducts.reduce(
      (sum, product) => sum + product.current,
      0,
    ),
    attainmentGoal: attainmentProducts.reduce(
      (sum, product) => sum + product.goal,
      0,
    ),
    attainmentProducts,
    topWriters: mapTopWriters(report.top_product_advocates_growers),
    breadthDepth: mapBreadthDepth(report.length_breadth),
    productSplits: mapProductSplits(
      report.key_metrics,
      selectedProduct,
      productKeys,
    ),
    competitiveDynamics: mapCompetitiveDynamics(
      report.competitive_dynamics,
      selectedProduct,
    ),
    competitiveFlags: strategyFields.competitiveFlags ?? [],
    payorDynamics: strategyFields.payorDynamics ?? "",
    measuresOfSuccess: strategyFields.measuresOfSuccess ?? "",
  };
}

export default mapQbrReportToTerritoryOverview;
